import {
  useRef,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";

interface PullToRefreshProps {
  children: ReactNode;
  /** 刷新回调，返回 Promise 或 void */
  onRefresh?: () => Promise<void> | void;
  /** 触发刷新的下拉距离阈值（px），默认 70 */
  threshold?: number;
  /** 最大下拉距离（px），默认 120 */
  maxPull?: number;
  /** 主色，默认 #22c55e */
  color?: string;
}

type Phase = "idle" | "pulling" | "ready" | "refreshing" | "done";

const PRIMARY = "#22c55e";

/**
 * 移动端下拉刷新组件
 * 仅在页面滚动到顶部时触发，支持触摸和鼠标拖拽
 */
export default function PullToRefresh({
  children,
  onRefresh,
  threshold = 70,
  maxPull = 120,
  color = PRIMARY,
}: PullToRefreshProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const currentPull = useRef(0);
  const [pull, setPull] = useState(0);
  const [phase, setPhase] = useState<Phase>("idle");
  const isTracking = useRef(false);

  const isAtTop = useCallback(() => {
    return window.scrollY <= 0;
  }, []);

  // 阻尼函数：越拉越难
  const dampen = useCallback(
    (dy: number) => {
      const ratio = Math.min(dy / maxPull, 1);
      return maxPull * (1 - Math.pow(1 - ratio, 2.5)) * 0.55;
    },
    [maxPull]
  );

  const handleStart = useCallback(
    (clientY: number) => {
      if (!isAtTop() || phase === "refreshing") return;
      startY.current = clientY;
      isTracking.current = true;
    },
    [isAtTop, phase]
  );

  const handleMove = useCallback(
    (clientY: number) => {
      if (!isTracking.current || phase === "refreshing") return;
      const dy = clientY - startY.current;
      if (dy <= 0) {
        if (pull > 0) {
          setPull(0);
          currentPull.current = 0;
          setPhase("idle");
        }
        return;
      }
      // 只在页面顶部时才响应
      if (!isAtTop()) return;

      const dampened = dampen(dy);
      currentPull.current = dampened;
      setPull(dampened);
      setPhase(dampened >= threshold ? "ready" : "pulling");
    },
    [phase, pull, isAtTop, dampen, threshold]
  );

  const handleEnd = useCallback(async () => {
    if (!isTracking.current) return;
    isTracking.current = false;

    if (phase === "ready" && onRefresh) {
      setPhase("refreshing");
      setPull(50); // 收缩到刷新位置
      try {
        await onRefresh();
      } catch {
        // ignore
      }
      setPhase("done");
      // 短暂显示完成状态
      await new Promise(r => setTimeout(r, 400));
    }
    setPull(0);
    currentPull.current = 0;
    setPhase("idle");
  }, [phase, onRefresh]);

  // Touch events
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onTouchStart = (e: TouchEvent) => handleStart(e.touches[0].clientY);
    const onTouchMove = (e: TouchEvent) => {
      handleMove(e.touches[0].clientY);
      // 防止页面滚动
      if (currentPull.current > 5 && isAtTop()) {
        e.preventDefault();
      }
    };
    const onTouchEnd = () => handleEnd();

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [handleStart, handleMove, handleEnd, isAtTop]);

  const progress = Math.min(pull / threshold, 1);
  const rotation = pull * 4; // spinner 旋转角度

  return (
    <div ref={containerRef} className="relative">
      {/* 下拉指示器 */}
      <div
        className="absolute left-0 right-0 flex flex-col items-center justify-end overflow-hidden pointer-events-none z-40"
        style={{
          top: 56, // 在 TopHeader 下方
          height: pull,
          transition:
            phase === "refreshing" || phase === "done" || phase === "idle"
              ? "height 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
              : "none",
        }}
      >
        <div className="flex flex-col items-center gap-1.5 pb-3">
          {/* 旋转圆环 */}
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            className="transition-opacity duration-200"
            style={{
              opacity: pull > 10 ? 1 : 0,
              transform:
                phase === "refreshing" ? undefined : `rotate(${rotation}deg)`,
              animation:
                phase === "refreshing"
                  ? "ptr-spin 0.8s linear infinite"
                  : "none",
            }}
          >
            <circle
              cx="14"
              cy="14"
              r="11"
              fill="none"
              stroke={`${color}20`}
              strokeWidth="2.5"
            />
            <circle
              cx="14"
              cy="14"
              r="11"
              fill="none"
              stroke={color}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={`${69 * progress} 69`}
              style={{
                transition:
                  phase === "refreshing"
                    ? "none"
                    : "stroke-dasharray 0.1s ease-out",
                filter: `drop-shadow(0 0 4px ${color}60)`,
              }}
            />
          </svg>

          {/* 文字提示 */}
          <span
            className="text-[10px] font-semibold tracking-wide transition-all duration-200"
            style={{
              color:
                phase === "ready" || phase === "refreshing" ? color : "#64748b",
              opacity: pull > 20 ? 1 : 0,
              transform: `translateY(${pull > 20 ? 0 : 8}px)`,
            }}
          >
            {phase === "refreshing"
              ? "刷新中..."
              : phase === "done"
                ? "✓ 已更新"
                : phase === "ready"
                  ? "释放刷新"
                  : "下拉刷新"}
          </span>
        </div>
      </div>

      {/* 内容区域 */}
      <div
        style={{
          transform: `translateY(${pull > 0 ? pull * 0.3 : 0}px)`,
          transition:
            phase === "refreshing" || phase === "done" || phase === "idle"
              ? "transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
              : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}
